import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { identityApiRef, configApiRef } from '@backstage/core-plugin-api';

export const ProjectJPField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<any>) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);

  const [projects, setProjects] = useState<any[]>([]);
  const [_project, setProject] = useState('');
  const [projectRef, setProjectRef] = useState('');

  // -----------------------------------------------------------------------------------
  // ✔️ 1. Sincronizar con formData
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;

    setProject(formData.project || '');
    setProjectRef(formData.projectRef || '');
  }, [formData]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2. Cargar proyectos donde el usuario es JP desde OpenFGA
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Obtener identidad del usuario
        const identity = await identityApi.getBackstageIdentity();
        const userRef = identity.userEntityRef;
        
        // Obtener email del usuario desde el catálogo
        let userEmail = identity.userEntityRef.split('/').pop() || '';
        try {
          const userEntity = await catalogApi.getEntityByRef(userRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) {
            userEmail = profile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch user entity, using username instead:', err);
        }

        // Verificar impersonación (si está habilitada)
        const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
        const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
        const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
        
        let effectiveUser = impersonatedUser || userEmail;
        if (impersonatedUser && impersonatedUser !== userEmail) {
          try {
            const impersonatedUserRef = `user:default/${impersonatedUser}`;
            const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
            const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
            if (impersonatedProfile?.email) {
              effectiveUser = impersonatedProfile.email;
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
          }
          console.log('🎭 Impersonating user:', effectiveUser);
        }

        // Llamar a OpenFGA para obtener proyectos donde el usuario es JP
        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;
        
        const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${effectiveUser}`,
            type: 'project',
            relation: 'jp',
          }),
        });

        if (!response.ok) {
          console.error('❌ Failed to fetch projects from OpenFGA');
          setProjects([]);
          return;
        }

        const data = await response.json();
        const projectCodesFromFGA = (data.objects ?? [])
          .map((p: string) => {
            // OpenFGA devuelve formato: "project:CÓDIGO"
            const match = p.match(/^project:(.+)$/i);
            return match ? match[1].toUpperCase() : null;
          })
          .filter(Boolean) as string[];

        console.log('🔐 Projects from OpenFGA (jp):', projectCodesFromFGA);

        // Obtener las entidades de proyecto del catálogo
        const resp = await catalogApi.getEntities({
          filter: { kind: 'Project' },
        });

        // Filtrar solo los proyectos que están en OpenFGA y no tienen parent
        const filteredProjects = resp.items.filter((p: any) => {
          const projectCode = p.spec?.number;
          return projectCode && 
                 projectCodesFromFGA.includes(String(projectCode).toUpperCase()) &&
                 !p.spec?.parent;
        });

        console.log('📋 Filtered projects:', filteredProjects.length);
        setProjects(filteredProjects);
      } catch (error) {
        console.error('❌ Error fetching projects:', error);
        setProjects([]);
      }
    };

    fetchProjects();
  }, [identityApi, catalogApi, config]);

  // -----------------------------------------------------------------------------------
  // ✔️ 3. Handle selección de proyecto
  // -----------------------------------------------------------------------------------
  const handleProjectChange = (proj: any) => {
    const ref = `project:${proj.metadata.namespace}/${proj.metadata.name}`;
    const number = proj.spec.number || '';

    setProject(number);
    setProjectRef(ref);

    onChange({
      project: number,
      projectRef: ref,
    });
  };

  // -----------------------------------------------------------------------------------
  // ✔️ Render
  // -----------------------------------------------------------------------------------
  return (
    <FormControl margin="normal" required={required} fullWidth>
      <Autocomplete
        options={projects}
        getOptionLabel={opt => opt.metadata.title || ''}
        value={
          projects.find(
            p => `project:${p.metadata.namespace}/${p.metadata.name}` === projectRef,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) handleProjectChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Project"
            required
            helperText={rawErrors?.[0] || 'Select the project where you are JP'}
          />
        )}
      />
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// ✔️ Validación
// -----------------------------------------------------------------------------------
export const projectJPCaseValidation = (
  value: { project?: string },
  validation: FieldValidation,
) => {
  if (!value?.project) validation.addError('Project is required.');
};

// -----------------------------------------------------------------------------------
// ✔️ Registro del Custom Field
// -----------------------------------------------------------------------------------
export const ProjectJPCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ProjectJPField',
    component: ProjectJPField,
    validation: projectJPCaseValidation,
  }),
);
